/**
 * List View Plugin for Native Interactive Site Map (ISM)
 *
 * Orchestrates the listing sheet panel: header (title), tab bar, and scrollable content area.
 * Writes into [data-panel] regardless of desktop sidebar or mobile drawer context. Also owns
 * VIEW_SOLD_FULL_LIST (NHS20-8826/8828's mobile "See All" full-screen sold listing) — it's a
 * variant of the same panel/header shell, not a separate feature, so it stays here instead of
 * spawning its own plugin.
 *
 * What to show (tab bar on/off, panel content) is not decided here — it's delegated to
 * services/listViewContentService.js#getListViewContent, keyed by active chip or by
 * VIEW_SOLD_FULL_LIST when that's the active view. This plugin only owns the panel's shared DOM
 * lifecycle (mount/unmount, header, tabs) and wires it to whichever content descriptor comes back.
 *
 * Responsibilities:
 * - Inject listing header, tab bar, and content wrapper into [data-panel]
 * - Wire tab click events to Redux dispatch (tabSelected action)
 * - Wire the Sold listing's "See All" button to navigateTo(VIEW_SOLD_FULL_LIST) and that view's
 *   Back button to navigateBack()
 * - Subscribe to store$ to keep tab counts, active state, and the header title in sync
 * - Clean up on stop
 *
 * @module nativeInteractiveSiteMap/plugins/listView.plugin
 */

import { EMPTY, fromEvent } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map } from 'rxjs/operators';
import createListingHeader from '../components/listingHeader.dom';
import createListingContent from '../components/listingContent.dom';
import {
  selectFilteredHousingUnits,
  selectFilteredHousePlans,
  selectSelectedTab,
  selectHousePlanCount,
  selectHousingUnitCount,
  selectActiveView,
  selectActiveViewContext,
  selectActiveChip,
  selectSelectedLotId,
  selectIsLotInventorySettled,
} from '../redux/selectors';
import getListingTitle from '../services/listingTitleService';
import getListViewContent from '../services/listViewContentService';
import createTabsDom from '../components/tabs.dom';
import createTabInteractionHandlers from '../handlers/tabInteractionHandlers';
import createAvailableTabFallbackEpic from '../redux/availableTabFallbackEpic';
import registerEpicOnce from '../redux/registerEpicOnce';
import LotStatus from '../enums/lotStatus';
import {
  TAB_FLOOR_PLANS,
  TAB_QUICK_MOVE_INS,
  VIEW_LISTING,
  VIEW_SOLD_FULL_LIST,
  MAX_SOLD_HOMES_MOBILE,
} from '../constants';

export default function createListViewPlugin() {
  const state = {
    header: null,
    tabs: null,
    content: null,
    panel: null,
    isMobile: false,
    store$: null,
    getState: null,
    dispatch: null,
    dataActions: null,
    seeAllClickSubscription: null,
    similarHomesToggleSubscription: null,
    subscriptions: [],
  };

  /**
   * Shows/hides the header's See All button (components/listingHeader.dom.js) based on whether
   * the Sold listing currently has more sold homes than fit on mobile — NHS20-8826. Lives here,
   * the one choke point every chip's render function already funnels through, so it stays in
   * sync on every trigger (chip switch, house-plans/housing-units changes, lot selection) without
   * each render function having to remember to call it.
   *
   * Only relevant to VIEW_LISTING — renderSoldFullListContent (the "See All" destination itself)
   * funnels through this same renderCards/syncSeeAllButton path with the same uncapped sold-homes
   * count, so without this check the button would show a "See All" pointing at the screen the
   * user is already on, next to the back button that already gets them out of it.
   */
  function syncSeeAllButton(cards) {
    const shouldShow = state.isMobile &&
      selectActiveView(state.getState()) === VIEW_LISTING &&
      selectActiveChip(state.getState()) === LotStatus.Sold.toString() &&
      Array.isArray(cards) && cards.length > MAX_SOLD_HOMES_MOBILE;

    if (shouldShow) {
      state.header?.showSeeAllButton();
    } else {
      state.header?.hideSeeAllButton();
    }
  }

  function renderCards(cards, renderGrid) {
    if (!state.content) {
      return;
    }

    /*
     * Centralizes the "empty vs still loading" decision for all renderers.
     * Delay renderGrid until lot inventory has settled to avoid premature empty states.
     */
    if ((!Array.isArray(cards) || cards.length === 0) && !selectIsLotInventorySettled(state.getState())) {
      return;
    }

    state.content.render(renderGrid(cards));
    syncSeeAllButton(cards);
  }

  const contentContext = { renderCards, isMobile: false };

  function onHousePlansChanged() {
    const currentState = state.getState();
    getListViewContent(currentState).renderOnHousePlansChanged(currentState, contentContext);
  }

  function onHousingUnitsChanged() {
    const currentState = state.getState();
    getListViewContent(currentState).renderOnHousingUnitsChanged(currentState, contentContext);
  }

  function onSelectedLotChanged() {
    const currentState = state.getState();
    getListViewContent(currentState).renderOnSelectedLotChanged(currentState, contentContext);
  }

  function createTabs(currentState) {
    const floorPlansLabel = 'Floor plans';
    const quickMoveInsLabel = 'Quick move-ins';

    /*
     * Counts read as 0 until the lot-inventory fetch settles. That's not a real "0 results", so the
     * badge stays blank rather than showing a misleading 0.
     * The gated subscription in setupSubscriptions fills in the real count once settled.
    */
    const isSettled = selectIsLotInventorySettled(currentState);

    state.tabs = createTabsDom([
      {
        label: floorPlansLabel,
        count: isSettled ? selectHousePlanCount(currentState) : '',
        tabValue: TAB_FLOOR_PLANS,
        isActive: selectSelectedTab(currentState) === TAB_FLOOR_PLANS,
      },
      {
        label: quickMoveInsLabel,
        count: isSettled ? selectHousingUnitCount(currentState) : '',
        tabValue: TAB_QUICK_MOVE_INS,
        isActive: selectSelectedTab(currentState) === TAB_QUICK_MOVE_INS,
      },
    ]);

    state.tabs.attachEventListeners(
      createTabInteractionHandlers((tabValue) => state.dispatch(state.dataActions.tabSelected(tabValue)))
    );

    state.panel.insertBefore(state.tabs.getElement(), state.content.getElement());
  }

  function removeTabs() {
    state.tabs?.remove();
    state.tabs = null;
  }

  function renderListing() {
    const currentState = state.getState();

    state.header = createListingHeader();
    state.content = createListingContent();

    state.panel.appendChild(state.header.getElement());
    state.panel.appendChild(state.content.getElement());

    if (getListViewContent(currentState).hasTabs) {
      createTabs(currentState);
    }

    state.header.setText(getListingTitle(selectActiveChip(currentState)));
    getListViewContent(currentState).renderContent(currentState, contentContext);
  }

  /**
   * VIEW_SOLD_FULL_LIST (NHS20-8826's "Mobile Full Sold State View"): full-screen, title "Sold",
   * Back button, every sold home uncapped. The filtering widget the ticket also calls for isn't
   * wired here — out of scope for this pass, no existing hook to reuse for it yet.
   */
  function renderSoldFullList() {
    const currentState = state.getState();

    state.header = createListingHeader();
    state.content = createListingContent();

    state.panel.appendChild(state.header.getElement());
    state.panel.appendChild(state.content.getElement());

    state.header.setText('Sold');
    state.header.showBackButton();
    state.header.onBackClick(() => state.dispatch(state.dataActions.navigateBack()));

    getListViewContent(currentState).renderContent(currentState, contentContext);
  }

  function remove() {
    state.header?.remove();
    state.tabs?.remove();
    state.content?.remove();
    state.header = null;
    state.tabs = null;
    state.content = null;
  }

  function onActiveViewChanged(activeView) {
    remove();
    if (activeView === VIEW_LISTING) {
      renderListing();
    } else if (activeView === VIEW_SOLD_FULL_LIST) {
      renderSoldFullList();
    }
  }

  function onSelectedTabChanged(selectedTab) {
    const currentState = state.getState();
    state.tabs.setActiveTab(selectedTab);
    getListViewContent(currentState).renderContent(currentState, contentContext);
  }

  function onActiveChipChanged(activeChip) {
    const currentState = state.getState();
    const tabsEnabled = getListViewContent(currentState).hasTabs;
    const hasTabs = state.tabs != null;

    state.header?.setText(getListingTitle(activeChip));

    if (tabsEnabled && !hasTabs) {
      createTabs(currentState);
    } else if (!tabsEnabled && hasTabs) {
      removeTabs();
    }

    getListViewContent(currentState).renderContent(currentState, contentContext);
  }

  function mapToTabCounts(storeState) {
    return {
      floorPlans: selectHousePlanCount(storeState),
      quickMoveIns: selectHousingUnitCount(storeState),
    };
  }

  function tabCountsEqual(previous, current) {
    return previous.floorPlans === current.floorPlans && previous.quickMoveIns === current.quickMoveIns;
  }

  function onTabCountsChanged({ floorPlans, quickMoveIns }) {
    state.tabs.updateCounts({
      [TAB_FLOOR_PLANS]: floorPlans,
      [TAB_QUICK_MOVE_INS]: quickMoveIns,
    });
  }

  /**
   * The Sold listing's "See All" button (services/listViewContentService.js#renderSoldContent)
   * only exists inside [data-panel]'s rendered markup, so it's caught here via event delegation
   * on the panel itself — same pattern as floorPlanDetail.plugin.js's expand-button listener.
   * Preserves the current activeViewContext (selected lot) across the navigation, matching
   * NHS20-8828's "preserve the selected lot" rule.
   */
  function setupSeeAllClickListener() {
    return fromEvent(state.panel, 'click')
      .pipe(filter((event) => event.target?.closest?.('[data-sold-see-all-button]') != null))
      .subscribe(() => {
        const currentState = state.getState();
        state.dispatch(state.dataActions.navigateTo({
          view: VIEW_SOLD_FULL_LIST,
          context: { ...selectActiveViewContext(currentState) },
        }));
      });
  }

  /**
   * The Sold listing's per-card "Similar homes available" expand/collapse toggle
   * (components/similarHomesSection.dom.js) — caught here via event delegation on the panel,
   * same pattern as setupSeeAllClickListener above (and floorPlanDetail.plugin.js's expand-button
   * listener). Purely a DOM class/attribute flip, not Redux state: which section renders expanded
   * by default is already decided per-render by services/listViewContentService.js
   * (renderSoldHomeCards); this only handles the user toggling it afterward, and that toggle
   * doesn't need to survive a re-render.
   */
  function setupSimilarHomesToggleListener() {
    return fromEvent(state.panel, 'click')
      .pipe(filter((event) => event.target?.closest?.('[data-similar-homes-toggle]') != null))
      .subscribe((event) => {
        const toggleButton = event.target?.closest?.('[data-similar-homes-toggle]');
        const section = toggleButton?.closest('.nhs-c-native-interactive-site-map__similar-homes-section');
        if (!toggleButton || !section) {
          return;
        }
        const willExpand = toggleButton.getAttribute('aria-expanded') !== 'true';

        toggleButton.setAttribute('aria-expanded', String(willExpand));
        section.classList.toggle('nhs-c-native-interactive-site-map__similar-homes-section--expanded', willExpand);
      });
  }

  function setupSubscriptions() {
    state.subscriptions.push(
      state.store$.pipe(
        filter(() => state.content != null),
        map(selectFilteredHousePlans),
        distinctUntilChanged(),
        catchError(() => EMPTY)
      ).subscribe(onHousePlansChanged),

      state.store$.pipe(
        filter(() => state.content != null),
        map(selectFilteredHousingUnits),
        distinctUntilChanged(),
        catchError(() => EMPTY)
      ).subscribe(onHousingUnitsChanged),

      state.store$.pipe(
        filter(() => state.content != null),
        map(selectSelectedLotId),
        distinctUntilChanged(),
        catchError(() => EMPTY)
      ).subscribe(onSelectedLotChanged),

      state.store$.pipe(
        map(selectActiveView),
        distinctUntilChanged(),
      ).subscribe(onActiveViewChanged),

      state.store$.pipe(
        filter(() => state.tabs != null),
        map(selectSelectedTab),
        distinctUntilChanged(),
      ).subscribe(onSelectedTabChanged),

      state.store$.pipe(
        filter(() => state.content != null),
        map(selectActiveChip),
        distinctUntilChanged(),
      ).subscribe(onActiveChipChanged),

      state.store$.pipe(
        filter((storeState) => state.tabs != null && selectIsLotInventorySettled(storeState)),
        map(mapToTabCounts),
        distinctUntilChanged(tabCountsEqual),
      ).subscribe(onTabCountsChanged),
    );
  }

  return {
    name: 'list-view',

    async init(context) {
      context.state.listView = {
        started: false,
        ready: false,
      };

      const availableTabFallbackEpic = createAvailableTabFallbackEpic({
        dataActions: context.services.dataActions,
      });
      registerEpicOnce(context.services.epic$, 'available-tab-fallback', availableTabFallbackEpic);
    },

    async start(context) {
      if (context.state.listView?.started) {
        return;
      }

      state.panel = context.root.querySelector('[data-panel]');
      if (!state.panel) {
        return;
      }

      context.state.listView = { started: true, ready: false };

      const { getState, dispatch, dataActions, store$ } = context.services;
      state.getState = getState;
      state.dispatch = dispatch;
      state.dataActions = dataActions;
      state.store$ = store$;
      state.isMobile = context.state.isMobile;
      contentContext.isMobile = state.isMobile;

      setupSubscriptions();
      state.seeAllClickSubscription = setupSeeAllClickListener();
      state.similarHomesToggleSubscription = setupSimilarHomesToggleListener();

      context.state.listView.ready = true;
    },

    async stop(context) {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];
      state.seeAllClickSubscription?.unsubscribe?.();
      state.seeAllClickSubscription = null;
      state.similarHomesToggleSubscription?.unsubscribe?.();
      state.similarHomesToggleSubscription = null;
      state.header?.remove();
      state.tabs?.remove();
      state.content?.remove();
      state.header = null;
      state.tabs = null;
      state.content = null;
      state.panel = null;
      state.store$ = null;
      state.getState = null;
      state.dispatch = null;
      state.dataActions = null;
      context.state.listView = { started: false, ready: false };
    },
  };
}
